import React, { useEffect, useMemo, useRef, useState } from 'react';
import './DateOfBirthPicker.css';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];
const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const MIN_AGE = 18;
const MAX_AGE = 80;

function pad2(n) { return n.toString().padStart(2, '0'); }

function parseISO(value) {
  if (!value) return { year: null, month: null, day: null };
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!m) return { year: null, month: null, day: null };
  return { year: +m[1], month: +m[2], day: +m[3] };
}

function toISO(year, month, day) {
  if (!year || !month || !day) return '';
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

function daysInMonth(year, month) {
  // month 1-12
  return new Date(year, month, 0).getDate();
}

function formatLong(year, month, day) {
  if (!year || !month || !day) return '';
  return `${day} de ${MONTH_NAMES[month - 1].toLowerCase()} de ${year}`;
}

export default function DateOfBirthPicker({ value, onChange, error }) {
  const parsed = parseISO(value);
  const [year, setYear] = useState(parsed.year);
  const [month, setMonth] = useState(parsed.month);
  const [day, setDay] = useState(parsed.day);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('year'); // 'year' | 'month' | 'day'
  const wrapRef = useRef(null);

  // Sync local state when external value changes (e.g., reset)
  useEffect(() => {
    const p = parseISO(value);
    setYear(p.year);
    setMonth(p.month);
    setDay(p.day);
  }, [value]);

  // Click outside → close (without losing partial selection)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const currentYear = new Date().getFullYear();
  const maxYear = currentYear - MIN_AGE;
  const minYear = currentYear - MAX_AGE;

  // Years: descending from maxYear → minYear
  const years = useMemo(() => {
    const arr = [];
    for (let y = maxYear; y >= minYear; y--) arr.push(y);
    return arr;
  }, [maxYear, minYear]);

  // Days adjusted to selected year/month (or 31 if not yet picked)
  const daysCount = year && month ? daysInMonth(year, month) : 31;
  const days = useMemo(() => Array.from({ length: daysCount }, (_, i) => i + 1), [daysCount]);

  function openPicker() {
    if (open) return;
    // Resume on the first incomplete step
    const firstMissing = !year ? 'year' : !month ? 'month' : 'day';
    setStep(firstMissing);
    setOpen(true);
  }

  function pickYear(y) {
    setYear(y);
    // If month/day previously chosen, validate they still fit (e.g., Feb 29)
    if (month && day) {
      const max = daysInMonth(y, month);
      if (day > max) setDay(null);
    }
    setStep('month');
  }

  function pickMonth(m) {
    setMonth(m);
    if (year && day) {
      const max = daysInMonth(year, m);
      if (day > max) setDay(null);
    }
    setStep('day');
  }

  function pickDay(d) {
    setDay(d);
    const iso = toISO(year, month, d);
    onChange?.(iso);
    setOpen(false);
  }

  const triggerLabel = formatLong(year, month, day) || 'Selecciona tu fecha';
  const triggerEmpty = !year || !month || !day;

  return (
    <div className={`dobp ${open ? 'dobp--open' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className={`dobp__trigger ${error ? 'dobp__trigger--error' : ''} ${triggerEmpty ? 'dobp__trigger--empty' : ''}`}
        onClick={openPicker}
        aria-expanded={open}
      >
        <span className="dobp__trigger-icon" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
            <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </span>
        <span className="dobp__trigger-label">{triggerLabel}</span>
        <span className="dobp__trigger-chevron" data-open={open}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>

      {open && (
        <div className="dobp__panel">
          {/* Breadcrumb / steps */}
          <div className="dobp__crumbs">
            <button
              type="button"
              className={`dobp__crumb ${step === 'year' ? 'dobp__crumb--active' : ''}`}
              onClick={() => setStep('year')}
            >
              <span className="dobp__crumb-label">Año</span>
              <span className="dobp__crumb-value">{year || '—'}</span>
            </button>
            <span className="dobp__crumb-sep">›</span>
            <button
              type="button"
              className={`dobp__crumb ${step === 'month' ? 'dobp__crumb--active' : ''}`}
              onClick={() => year && setStep('month')}
              disabled={!year}
            >
              <span className="dobp__crumb-label">Mes</span>
              <span className="dobp__crumb-value">{month ? MONTH_SHORT[month - 1] : '—'}</span>
            </button>
            <span className="dobp__crumb-sep">›</span>
            <button
              type="button"
              className={`dobp__crumb ${step === 'day' ? 'dobp__crumb--active' : ''}`}
              onClick={() => year && month && setStep('day')}
              disabled={!year || !month}
            >
              <span className="dobp__crumb-label">Día</span>
              <span className="dobp__crumb-value">{day || '—'}</span>
            </button>
          </div>

          {/* Step content */}
          {step === 'year' && (
            <div className="dobp__step">
              <div className="dobp__step-title">¿En qué año naciste?</div>
              <div className="dobp__grid dobp__grid--years">
                {years.map((y) => (
                  <button
                    key={y}
                    type="button"
                    className={`dobp__cell ${y === year ? 'dobp__cell--selected' : ''}`}
                    onClick={() => pickYear(y)}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 'month' && (
            <div className="dobp__step">
              <div className="dobp__step-title">¿Qué mes?</div>
              <div className="dobp__grid dobp__grid--months">
                {MONTH_NAMES.map((name, i) => {
                  const m = i + 1;
                  return (
                    <button
                      key={m}
                      type="button"
                      className={`dobp__cell ${m === month ? 'dobp__cell--selected' : ''}`}
                      onClick={() => pickMonth(m)}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 'day' && (
            <div className="dobp__step">
              <div className="dobp__step-title">¿Qué día?</div>
              <div className="dobp__grid dobp__grid--days">
                {days.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`dobp__cell ${d === day ? 'dobp__cell--selected' : ''}`}
                    onClick={() => pickDay(d)}
                  >
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
